import React, { useState, useMemo } from 'react';
import Modal from '../common/Modal';
import { Customer } from '../../types/customer';
import { MapPinIcon, BoltIcon, HomeIcon, ClipboardDocumentListIcon, ArrowDownTrayIcon, DocumentTextIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// Initialize pdfMake with fonts
(pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs || (pdfFonts as any);

interface PodDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer;
}

const PodDetailsModal: React.FC<PodDetailsModalProps> = ({
  isOpen,
  onClose,
  customer
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const formatNumber = (value: string): string => {
    if (!value) return '';
    
    const cleanValue = value.replace(/[^0-9.]/g, '');
    const parts = cleanValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    
    return parts.length > 1 ? parts.join('.') : parts[0];
  };

  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const customerName = customer.type === 'organization' 
        ? customer.companyName 
        : `${customer.firstName} ${customer.lastName}`;

      const excelData = allPods.map((pod: any, index: number) => ({
        'Típus': pod.isPrimary ? 'Fő POD' : `További POD #${index}`,
        'POD Azonosító': pod.id || '-',
        'Cím': pod.address || '-',
        'Mérés típusa': pod.measurementType || '-',
        'Éves fogyasztás (kWh)': pod.annualConsumption || '-',
        'Szorzó': pod.multiplier || '-',
        'Számlázható': pod.billable !== undefined ? (pod.billable ? 'Igen' : 'Nem') : '-',
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'POD Adatok');

      const colWidths = [
        { wch: 15 }, { wch: 25 }, { wch: 40 }, { wch: 20 }, 
        { wch: 20 }, { wch: 10 }, { wch: 15 }
      ];
      ws['!cols'] = colWidths;

      const fileName = `POD_Adatok_${customerName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success('Excel fájl sikeresen exportálva!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Hiba történt az Excel exportálás során');
    }
  };

  const exportToPDF = () => {
    try {
      const customerName = customer.type === 'organization' 
        ? customer.companyName 
        : `${customer.firstName} ${customer.lastName}`;

      const tableBody: any[] = [
        [
          { text: 'Típus', style: 'tableHeader' },
          { text: 'POD ID', style: 'tableHeader' },
          { text: 'Cím', style: 'tableHeader' },
          { text: 'Mérés típusa', style: 'tableHeader' },
          { text: 'Éves fogyasztás', style: 'tableHeader' }
        ]
      ];

      allPods.forEach((pod: any, index: number) => {
        tableBody.push([
          pod.isPrimary ? 'Fő POD' : `További POD #${index}`,
          pod.id || '-',
          pod.address || '-',
          pod.measurementType || '-',
          pod.annualConsumption ? `${formatNumber(pod.annualConsumption)} kWh` : '-'
        ]);
      });

      const docDefinition: any = {
        pageSize: 'A4',
        pageOrientation: 'landscape',
        pageMargins: [40, 60, 40, 60],
        content: [
          {
            text: 'POD Információk',
            style: 'header',
            alignment: 'center',
            margin: [0, 0, 0, 10]
          },
          {
            text: customerName,
            style: 'subheader',
            alignment: 'center',
            margin: [0, 0, 0, 20]
          },
          {
            text: `Exportálva: ${new Date().toLocaleDateString('hu-HU')}`,
            style: 'date',
            alignment: 'right',
            margin: [0, 0, 0, 20]
          },
          {
            table: {
              headerRows: 1,
              widths: ['auto', '*', '*', 'auto', 'auto'],
              body: tableBody
            },
            layout: {
              fillColor: function (rowIndex: number) {
                return rowIndex === 0 ? '#4F46E5' : (rowIndex % 2 === 0 ? '#F3F4F6' : null);
              },
              hLineWidth: function () { return 0.5; },
              vLineWidth: function () { return 0.5; },
              hLineColor: function () { return '#E5E7EB'; },
              vLineColor: function () { return '#E5E7EB'; }
            }
          },
          {
            text: `Összes mérési pont: ${allPods.length}`,
            style: 'summary',
            margin: [0, 20, 0, 5]
          },
          {
            text: `Összes éves fogyasztás: ${formatNumber(totalConsumption.toString())} kWh`,
            style: 'summary'
          }
        ],
        styles: {
          header: { fontSize: 18, bold: true, color: '#1F2937' },
          subheader: { fontSize: 14, bold: true, color: '#4B5563' },
          date: { fontSize: 10, color: '#6B7280', italics: true },
          tableHeader: { bold: true, fontSize: 11, color: 'white', fillColor: '#4F46E5' },
          summary: { fontSize: 12, bold: true, color: '#374151' }
        },
        defaultStyle: { fontSize: 10, color: '#1F2937' }
      };

      (pdfMake as any).createPdf(docDefinition).download(`POD_Adatok_${customerName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF fájl sikeresen exportálva!');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Hiba történt a PDF exportálás során');
    }
  };

  const allPods: any[] = [];
  
  if (customer.pod) {
    allPods.push({ ...customer.pod, isPrimary: true });
  }
  
  if (customer.additionalPods && customer.additionalPods.length > 0) {
    allPods.push(...customer.additionalPods.map(pod => ({ ...pod, isPrimary: false })));
  }

  // Szűrt POD-ok
  const filteredPods = useMemo(() => {
    if (!searchTerm) return allPods;
    
    const lowerSearch = searchTerm.toLowerCase();
    return allPods.filter(pod => 
      (pod.id && pod.id.toLowerCase().includes(lowerSearch)) ||
      (pod.address && pod.address.toLowerCase().includes(lowerSearch)) ||
      (pod.measurementType && pod.measurementType.toLowerCase().includes(lowerSearch))
    );
  }, [searchTerm, allPods.length]);

  const totalConsumption = allPods.reduce((sum, pod) => {
    return sum + parseFloat(pod.annualConsumption || '0');
  }, 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="POD Információk - Részletes nézet"
      size="6xl"
    >
      <div className="p-6">
        <div className="mb-6 p-4 bg-gradient-to-r from-violet-50 to-violet-100 rounded-xl border border-violet-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <MapPinIcon className="h-6 w-6 text-violet-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {customer.type === 'organization' ? customer.companyName : `${customer.firstName} ${customer.lastName}`}
                </h3>
                <p className="text-sm text-gray-600">POD Mérési Pontok</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-violet-600">{allPods.length}</div>
              <div className="text-sm text-gray-600">Mérési pont</div>
            </div>
          </div>
          
          <div className="flex gap-2 pt-3 border-t border-violet-200">
            <button
              onClick={exportToExcel}
              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm transition-colors shadow-sm"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Excel Export
            </button>
            <button
              onClick={exportToPDF}
              className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm transition-colors shadow-sm"
            >
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              PDF Export
            </button>
          </div>
          
          {totalConsumption > 0 && (
            <div className="mt-4 pt-4 border-t border-violet-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Összes éves fogyasztás:</span>
                <span className="font-semibold text-gray-900">
                  {formatNumber(totalConsumption.toString())} kWh
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Keresőmező */}
        {allPods.length > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-violet-50 via-purple-50 to-pink-50 rounded-xl border-2 border-violet-200 shadow-md">
            <div className="flex items-center mb-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-violet-600 mr-2" />
              <h4 className="text-sm font-semibold text-violet-900">POD Szűrő</h4>
            </div>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-violet-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Keresés POD ID, cím vagy mérés típusa alapján..."
                className="w-full pl-10 pr-10 py-3 border-2 border-violet-300 rounded-lg 
                         focus:ring-2 focus:ring-violet-500 focus:border-violet-500 
                         bg-white shadow-sm text-gray-900 placeholder-violet-300
                         transition-all duration-200"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 
                           text-violet-400 hover:text-violet-600 hover:bg-violet-100 
                           rounded-full p-1 transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
            {searchTerm && (
              <div className="mt-3 flex items-center justify-between p-2 bg-white rounded-lg border border-violet-200">
                <p className="text-sm font-medium text-violet-700">
                  🔍 {filteredPods.length} találat {allPods.length} POD közül
                </p>
                {filteredPods.length === 0 && (
                  <span className="text-xs text-amber-600 font-medium">Nincs találat</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* POD List */}
        {filteredPods.length > 0 ? (
          <div className="space-y-4">
            {filteredPods.map((pod, index) => (
              <div 
                key={`${pod.id}-${index}`} 
                className={`p-6 rounded-xl border transition-all hover:shadow-md ${
                  pod.isPrimary 
                    ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200' 
                    : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-full mr-3 ${pod.isPrimary ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      {pod.isPrimary ? (
                        <BoltIcon className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ClipboardDocumentListIcon className="h-5 w-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        {pod.isPrimary ? 'Fő POD' : `További POD #${index}`}
                      </h4>
                      {pod.isPrimary && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                          Elsődleges mérési pont
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {pod.measurementType && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      pod.isPrimary 
                        ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                        : 'bg-gray-50 text-gray-700 border border-gray-200'
                    }`}>
                      {pod.measurementType}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">POD Azonosító</label>
                    <div className={`text-base font-mono bg-white px-3 py-2 rounded-lg border ${pod.isPrimary ? 'border-blue-200' : 'border-gray-200'}`}>
                      {pod.id || '-'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Éves fogyasztás</label>
                    <div className={`text-base bg-white px-3 py-2 rounded-lg border ${pod.isPrimary ? 'border-blue-200' : 'border-gray-200'}`}>
                      {pod.annualConsumption ? (
                        <span className="font-semibold text-gray-900">{formatNumber(pod.annualConsumption)} kWh</span>
                      ) : '-'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Mérés típusa</label>
                    <div className={`text-base bg-white px-3 py-2 rounded-lg border ${pod.isPrimary ? 'border-blue-200' : 'border-gray-200'}`}>
                      {pod.measurementType || '-'}
                    </div>
                  </div>
                </div>

                {pod.address && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      <HomeIcon className="h-4 w-4 inline mr-1" />
                      Cím (aggregált)
                    </label>
                    <div className={`text-base bg-white px-3 py-2 rounded-lg border ${pod.isPrimary ? 'border-blue-200' : 'border-gray-200'}`}>
                      {pod.address}
                    </div>
                  </div>
                )}
                {pod.partnerId && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Partneri azonosító
                    </label>
                    <div className="text-base bg-white px-3 py-2 rounded-lg border border-gray-200">
                      {pod.partnerId}
                    </div>
                  </div>
                )}
                {(pod.postalCode || pod.city || pod.street) && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-2">Szétbontott cím:</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      {pod.postalCode && (
                        <div>
                          <span className="text-gray-500 text-xs">Irányítószám:</span>
                          <div className="font-medium text-gray-900">{pod.postalCode}</div>
                        </div>
                      )}
                      {pod.city && (
                        <div>
                          <span className="text-gray-500 text-xs">Város:</span>
                          <div className="font-medium text-gray-900">{pod.city}</div>
                        </div>
                      )}
                      {pod.street && (
                        <div>
                          <span className="text-gray-500 text-xs">Utca, házszám:</span>
                          <div className="font-medium text-gray-900">{pod.street}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : searchTerm ? (
          <div className="text-center py-12">
            <MagnifyingGlassIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Nincs találat</h3>
            <p className="text-sm text-gray-500">Próbálj meg más keresési kifejezést használni.</p>
          </div>
        ) : (
          <div className="text-center py-12">
            <MapPinIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Nincsenek POD adatok</h3>
            <p className="text-sm text-gray-500">Ehhez az ügyfélhez nem tartoznak mérési pont információk.</p>
          </div>
        )}

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm transition-colors"
          >
            Bezárás
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PodDetailsModal;
